// M365Dashboard.jsx — Halaman lisensi Microsoft 365. Tab: Overview | User Detail | Pricing.
// Sumber data (read-only, ber-permission 'm365_license'):
//   GET /api/m365/summary            → { categories, summary, snapshot, users:{...}, domainGroups }
//   GET /api/m365/capacity           → { data:[{sku_part,friendly_name,total,assigned,available,percent}] }
//   GET /api/m365/distribution       → { categories, groups:[{key,label,rows,grand}] }
//   GET /api/m365/users              → { data:[{upn,domain,sku_part,license}], total }
//   GET /api/m365/user-detail        → { data:[{...18 kolom}], total, snapshot }
//   GET /api/m365/distribution-users → { data:[{upn,domain,license,display_name,department,enabled}], total }
//   GET /api/m365/pricing            → { data:[{sku_part,friendly_name,is_category,price_usd,notes,updated_at,updated_by}], canEdit }
//   PUT /api/m365/pricing/:sku_part  → butuh perm 'm365_pricing_edit' (didelegasikan terpisah dari 'm365_license')
// Fetch/tulis data lisensi dilakukan lewat cron / Run Now di Table Maintenance.
// Harga (Pricing) diedit langsung di tab ini oleh siapa pun yg punya 'm365_pricing_edit'.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, CartesianGrid, LabelList,
} from "recharts";
import * as XLSX from "xlsx";
import {
  RefreshCw, Users, BadgeCheck, UserX, Globe, X, LayoutGrid, Gauge, Wallet, Download, Search,
  Layers, Table2, BarChart3, PieChart as PieIcon, UserCog, Tag, Sparkles, AlertTriangle, CalendarClock, ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import useTabDeepLink from "../hooks/useTabDeepLink";

// ── Palet warna kategori ──────────────────────────────────────────────────────
// Tab yang boleh datang dari `?tab=` (QuickSearch). "pricing" dikecualikan.
const M365_TABS = ["overview", "detail", "analytics"];

const MC = ["rgb(var(--brand-600))", "#db2777", "#16a34a", "#7c3aed", "#dc2626", "#0891b2", "#0f766e", "#65a30d", "#9333ea", "#0ea5e9"];

// ── Style (light theme, selaras dgn dashboard lain) ─────────────────────────────
const S = {
  page:    { padding: "clamp(12px,4vw,20px) clamp(12px,4vw,24px)", fontFamily: "'Segoe UI',Arial,sans-serif", color: "#1e293b" },
  hdr:     { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 },
  title:   { fontSize: "clamp(17px,4vw,20px)", fontWeight: 800, color: "#0f172a", margin: 0 },
  sub:     { fontSize: 13, color: "#64748b", marginTop: 4 },
  card:    { background: "#fff", border: "1px solid #e8edf5", borderRadius: 16, padding: "16px 18px", boxShadow: "0 1px 2px rgba(15,23,42,.04), 0 6px 20px rgba(15,23,42,.05)" },
  cardT:   { fontSize: 11, fontWeight: 700, color: "#475569", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 14, marginBottom: 18 },
  grid2:   { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,360px),1fr))", gap: 16, marginBottom: 16 },
  pill:    { fontSize: 11, fontWeight: 700, color: "rgb(var(--brand-700))", background: "rgb(var(--brand-50))", padding: "3px 10px", borderRadius: 999, letterSpacing: 0, textTransform: "none" },
  secT:    { display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 800, color: "#334155", letterSpacing: 1, textTransform: "uppercase", margin: "6px 0 10px" },
  btn:     { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#475569", fontSize: 13, padding: "7px 12px", cursor: "pointer", fontFamily: "inherit" },
  input:   { padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "#fff" },
  th:      { padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, letterSpacing: .5, background: "#f8fafc", color: "#64748b", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" },
  td:      { padding: "8px 12px", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9" },
  tabBar:  { display: "inline-flex", gap: 4, marginBottom: 18, padding: 4, background: "#f1f5f9", borderRadius: 12, flexWrap: "wrap" },
  tab:     { display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#64748b", cursor: "pointer", border: "none", background: "transparent", borderRadius: 9, fontFamily: "inherit", transition: "all .15s" },
  tabOn:   { color: "rgb(var(--brand-700))", background: "#fff", boxShadow: "0 1px 3px rgba(15,23,42,.12)" },
};
// Warna lisensi konsisten di semua chart/tabel (berdasarkan nama).
const licColor = (name, list) => { const i = (list || []).indexOf(name); return MC[(i < 0 ? 0 : i) % MC.length]; };
const fmtUsd0 = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const CSS = `
  .m365-tr td { transition: background .12s; }
  .m365-tr:nth-child(even) td { background: #fafbfd; }
  .m365-tr:hover td { background: rgb(var(--brand-50)) !important; }
  .m365-cap:hover { box-shadow: 0 8px 22px rgba(15,23,42,.10) !important; transform: translateY(-1px); }
  @keyframes m365Spin { to { transform: rotate(360deg); } }
`;

function Chip({ children, color = "#475569", bg = "#f1f5f9" }) {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 600, color, background: bg, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>{children}</span>;
}
const TT = { contentStyle: { background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }, labelStyle: { color: "#94a3b8" }, itemStyle: { color: "#f1f5f9" } };

const API_BASE = window.__API_BASE__ || "";
const getToken = () => localStorage.getItem("auth_token") || localStorage.getItem("token") || "";
const authGet = async (path) => {
  const r = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
};

function KpiCard({ icon: Icon, label, value, color, sub, onClick }) {
  return (
    <div
      style={{ ...S.card, position: "relative", overflow: "hidden", cursor: onClick ? "pointer" : "default", transition: "box-shadow .2s, transform .2s",
               background: `linear-gradient(135deg, #fff 55%, color-mix(in srgb, ${color} 9%, #fff))` }}
      onClick={onClick}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = "0 10px 28px rgba(15,23,42,.12)"; e.currentTarget.style.transform = "translateY(-2px)"; } }}
      onMouseLeave={e => { if (onClick) { e.currentTarget.style.boxShadow = S.card.boxShadow; e.currentTarget.style.transform = "none"; } }}
      title={onClick ? "Click for details" : undefined}
    >
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: color }} />
      <div style={{ position: "absolute", right: -18, bottom: -18, opacity: .07 }}><Icon size={88} color={color} /></div>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: color, boxShadow: `0 6px 14px color-mix(in srgb, ${color} 35%, transparent)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={20} color="#fff" />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: .6 }}>{label}</div>
          <div style={{ fontSize: "clamp(20px,4vw,26px)", fontWeight: 800, color: "#0f172a", lineHeight: 1.15, marginTop: 2 }}>
            {typeof value === "number" ? value.toLocaleString() : value}
          </div>
          {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
        </div>
      </div>
    </div>
  );
}

// Tabel distribusi per-domain per grup lokasi. Sel angka & Total → klik drilldown.
function DistTable({ group, categories, onCell, costMap }) {
  if (!group.rows.length) return null;
  const showCost  = !!costMap;
  // Kolom In-Use (Workstation "In Use" dari SDP Assets) hanya dikirim backend
  // untuk grup Indonesia; grup lain tidak menampilkan kolomnya sama sekali.
  const showInUse = !!group.hasInUse;
  const th  = { padding: "9px 10px", textAlign: "center", fontSize: 11, fontWeight: 700, background: "rgb(var(--brand-600))", color: "#fff", whiteSpace: "nowrap", borderRight: "1px solid rgba(255,255,255,.25)", letterSpacing: .3 };
  const th2 = { padding: "6px 10px", textAlign: "center", fontSize: 10, fontWeight: 600, background: "rgb(var(--brand-500))", color: "#fff", whiteSpace: "nowrap", borderRight: "1px solid rgba(255,255,255,.25)" };
  const td  = { padding: "7px 10px", textAlign: "center", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9" };
  const sepBody = { borderLeft: "2px solid #e2e8f0" };   // pembatas antar license type (body)
  const sepHead = { borderLeft: "2px solid rgba(255,255,255,.55)" }; // pembatas di header biru
  const sepThin = { borderLeft: "1px solid #eef2f7" };   // pembatas tipis antara kolom Users & Cost(USD)
  const sepThinHead = { borderLeft: "1px solid rgba(255,255,255,.35)" }; // versi tipis untuk header biru
  const zebra = (i) => ({ background: i % 2 === 1 ? "#f8fafc" : "#fff" });
  const clickable = { cursor: "pointer", textDecoration: "underline", textDecorationColor: "#cbd5e1", textUnderlineOffset: 2 };
  const fmtUsd = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const costOf = (domain, license) => (costMap?.[domain]?.[license] ?? 0);
  const domainCostTotal = (domain) => categories.reduce((a, c) => a + costOf(domain, c), 0);
  let grandCost = 0;
  const grandCostByCat = {}; categories.forEach(c => { grandCostByCat[c] = 0; });

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Globe size={15} color="rgb(var(--brand-600))" />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{group.label}</span>
        <Chip>{group.rows.length} domains</Chip>
        <Chip color="rgb(var(--brand-700))" bg="rgb(var(--brand-50))">{(group.grand.total || 0).toLocaleString()} users</Chip>
      </div>
      <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
          <thead>
            <tr>
              <th rowSpan={showCost ? 2 : 1} style={{ ...th, textAlign: "left", borderTopLeftRadius: 10 }}>Domain</th>
              {categories.map(c => <th key={c} colSpan={showCost ? 2 : 1} style={{ ...th, ...sepHead }}>{c}</th>)}
              <th colSpan={showCost ? 2 : 1} style={{ ...th, ...sepHead, ...(showInUse ? {} : { borderTopRightRadius: 10 }) }}>Total Users</th>
            {showInUse && (
              <th rowSpan={showCost ? 2 : 1} style={{ ...th, ...sepHead, borderTopRightRadius: 10 }}>Workstation In Use</th>
            )}
            </tr>
            {showCost && (
              <tr>
                {categories.map(c => (
                  <React.Fragment key={c}>
                    <th style={{ ...th2, ...sepHead }}>Users</th>
                    <th style={{ ...th2, ...sepThinHead }}>Cost(USD)</th>
                  </React.Fragment>
                ))}
                <th style={{ ...th2, ...sepHead }}>Users</th>
                <th style={{ ...th2, ...sepThinHead }}>Cost(USD)</th>
              </tr>
            )}
          </thead>
          <tbody>
            {group.rows.map((r, i) => {
              if (showCost) categories.forEach(c => { grandCostByCat[c] += costOf(r.domain, c); });
              const rowCost = showCost ? domainCostTotal(r.domain) : 0;
              grandCost += rowCost;
              return (
                <tr key={r.domain} style={zebra(i)}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 600 }}>{r.domain}</td>
                  {categories.map(c => {
                    const n = r.counts[c] || 0;
                    return (
                      <React.Fragment key={c}>
                        <td style={{ ...td, ...sepBody, color: n ? "rgb(var(--brand-600))" : "#cbd5e1", ...(n ? clickable : {}) }}
                          onClick={n ? () => onCell(r.domain, c) : undefined}>{n}</td>
                        {showCost && <td style={{ ...td, ...sepThin, textAlign: "right", color: "#16a34a" }}>{costOf(r.domain, c) ? fmtUsd(costOf(r.domain, c)) : "—"}</td>}
                      </React.Fragment>
                    );
                  })}
                  <td style={{ ...td, ...sepBody, fontWeight: 700, color: "rgb(var(--brand-600))", ...(r.total ? clickable : {}) }}
                    onClick={r.total ? () => onCell(r.domain, "ALL") : undefined}>{r.total}</td>
                  {showCost && <td style={{ ...td, ...sepThin, textAlign: "right", fontWeight: 700, color: "#16a34a" }}>{rowCost ? fmtUsd(rowCost) : "—"}</td>}
                  {showInUse && (
                    <td style={{ ...td, ...sepBody, fontWeight: 700, color: r.inUse == null ? "#cbd5e1" : "#0f172a" }}>
                      {r.inUse == null ? "—" : r.inUse}
                    </td>
                  )}
                </tr>
              );
            })}
            <tr style={{ background: "#f1f5f9" }}>
              <td style={{ ...td, textAlign: "left", fontWeight: 800 }}>GRAND TOTAL</td>
              {categories.map(c => (
                <React.Fragment key={c}>
                  <td style={{ ...td, ...sepBody, fontWeight: 800 }}>{group.grand.counts[c] || 0}</td>
                  {showCost && <td style={{ ...td, ...sepThin, textAlign: "right", fontWeight: 800, color: "#16a34a" }}>{grandCostByCat[c] ? fmtUsd(grandCostByCat[c]) : "—"}</td>}
                </React.Fragment>
              ))}
              <td style={{ ...td, ...sepBody, fontWeight: 800 }}>{group.grand.total}</td>
              {showCost && <td style={{ ...td, ...sepThin, textAlign: "right", fontWeight: 800, color: "#16a34a" }}>{grandCost ? fmtUsd(grandCost) : "—"}</td>}
                {showInUse && (
                  <td style={{ ...td, ...sepBody, fontWeight: 800 }}>{group.grand.inUse || 0}</td>
                )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Kartu kapasitas per SKU (hijau brand <90%, oranye ≥90%, merah ≥100%).
function CapacityCard({ row }) {
  const pct = Math.min(row.percent, 100);
  const color = row.percent >= 100 ? "#dc2626" : row.percent >= 90 ? "#d97706" : "rgb(var(--brand-600))";
  const state = row.percent >= 100 ? { txt: "Full", c: "#b91c1c", bg: "#fee2e2" }
              : row.percent >= 90  ? { txt: "Near full", c: "#b45309", bg: "#fef3c7" }
              : row.percent < 50   ? { txt: "Low use", c: "#0369a1", bg: "#e0f2fe" }
              : { txt: "Healthy", c: "#15803d", bg: "#dcfce7" };
  return (
    <div className="m365-cap" style={{ border: "1px solid #e8edf5", borderRadius: 14, padding: "12px 14px", background: "#fff", transition: "box-shadow .2s, transform .2s", boxShadow: "0 1px 2px rgba(15,23,42,.04)" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>{row.friendly_name}</div>
          <div style={{ fontSize: 10.5, color: "#94a3b8", fontFamily: "monospace", marginTop: 2 }}>{row.sku_part}</div>
        </div>
        <Chip color={state.c} bg={state.bg}>{state.txt}</Chip>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, margin: "10px 0 6px" }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{(row.assigned || 0).toLocaleString()}</span>
        <span style={{ fontSize: 12, color: "#64748b" }}>/ {(row.total || 0).toLocaleString()} assigned</span>
        <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 800, color }}>{row.percent}%</span>
      </div>
      <div style={{ height: 8, background: "#eef2f7", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width .6s" }} />
      </div>
      <div style={{ fontSize: 11.5, color: row.available <= 0 ? "#dc2626" : "#64748b", fontWeight: row.available <= 0 ? 700 : 500, marginTop: 7 }}>
        {row.available <= 0 ? "No seats available" : `${row.available.toLocaleString()} available`}
      </div>
    </div>
  );
}

// Modal drilldown: user pada (domain × license) dari License Distribution.
function DrillModal({ ctx, t, onClose, dataFetcher }) {
  const get = dataFetcher || authGet;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    const qs = new URLSearchParams({ domain: ctx.domain });
    if (ctx.license && ctx.license !== "ALL") qs.set("license", ctx.license);
    get(`/api/m365/distribution-users?${qs.toString()}`)
      .then(d => { if (alive) setRows(d.data || []); })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ctx]);

  const title = ctx.license === "ALL"
    ? `${ctx.domain} — ${t("m365.allLicenses")}`
    : `${ctx.domain} — ${ctx.license}`;

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 14, width: "min(720px,100%)", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 40px rgba(0,0,0,.2)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>{title}</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{rows.length} {t("m365.users")}</div>
          </div>
          <button onClick={onClose} style={{ ...S.btn, padding: 6 }}><X size={16} /></button>
        </div>
        <div style={{ overflowY: "auto", padding: "8px 20px 20px" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: 30 }}>{t("common.loading")}</div>
          ) : err ? (
            <div style={{ color: "#dc2626", padding: 16 }}>{err}</div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: 30 }}>{t("m365.noUsers")}</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={S.th}>UPN</th>
                  <th style={S.th}>{t("m365.colDisplayName")}</th>
                  <th style={S.th}>{t("m365.colDepartment")}</th>
                  {ctx.license === "ALL" && <th style={S.th}>{t("m365.colLicense")}</th>}
                  <th style={{ ...S.th, textAlign: "center" }}>{t("m365.colEnabled")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="m365-tr">
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.upn}</td>
                    <td style={S.td}>{r.display_name || "—"}</td>
                    <td style={S.td}>{r.department || "—"}</td>
                    {ctx.license === "ALL" && <td style={S.td}>{r.license}</td>}
                    <td style={{ ...S.td, textAlign: "center" }}>
                      {r.enabled === 1
                        ? <span style={{ color: "#16a34a", fontWeight: 600 }}>{t("m365.enabledYes")}</span>
                        : r.enabled === 0
                          ? <span style={{ color: "#dc2626", fontWeight: 600 }}>{t("m365.enabledNo")}</span>
                          : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const PAGE = 50;

// ── Analytics tab: 4 seksi (semua potret kondisi terkini) ───────────────────
function AnalyticsTab({ an, t, onRefresh, busy }) {
  const usd = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const th = { padding: "8px 10px", textAlign: "left", fontSize: 11, fontWeight: 700, background: "#f8fafc", color: "#64748b", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" };
  const td = { padding: "7px 10px", fontSize: 13, color: "#0f172a", borderBottom: "1px solid #f1f5f9" };
  const Kpi = (p) => <KpiCard {...p} />;

  const canPrice = !!an.canViewPrice;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          {an.snapshot ? `${t("m365.snapshot")}: ${an.snapshot}` : ""}
          {canPrice && !an.pricesSet && <span style={{ color: "#d97706", marginLeft: 8 }}>· {t("m365.anNoPrices")}</span>}
        </div>
        <button style={S.btn} onClick={onRefresh} disabled={busy}><RefreshCw size={14} /> {t("m365.refresh")}</button>
      </div>

      <div style={{ fontSize: 12, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", marginBottom: 14, lineHeight: 1.5 }}>
        ℹ️ {t("m365.anHint")}
      </div>

      {/* 1) Potensi penghematan */}
      <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anSavings")}</div>
      <div style={S.kpiGrid}>
        <Kpi icon={UserX}      color="#dc2626" label={t("m365.anUnlicensed")}       value={Number(an.savings.unlicensed).toLocaleString()} />
        <Kpi icon={UserX}      color="#d97706" label={t("m365.anDisabledLicensed")} value={Number(an.savings.disabledLicensed).toLocaleString()} />
        {canPrice && <Kpi icon={BadgeCheck} color="#16a34a" label={t("m365.anPotentialSaving")}  value={usd(an.savings.disabledSavingUsd)} sub={t("m365.anPerFetch")} />}
      </div>
      {an.savings.candidates.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anRevokeCandidates")}</div>
          <div style={{ overflowX: "auto", maxHeight: 320, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>UPN</th><th style={th}>Domain</th><th style={th}>{t("m365.colDepartment")}</th>
                <th style={th}>{t("m365.colLicenses")}</th>{canPrice && <th style={{ ...th, textAlign: "right" }}>{t("m365.colPriceUsd")}</th>}
              </tr></thead>
              <tbody>
                {an.savings.candidates.map((c, i) => (
                  <tr key={i}>
                    <td style={td}>{c.upn}</td>
                    <td style={td}>{c.domain}</td>
                    <td style={td}>{c.department || "—"}</td>
                    <td style={td}>{c.licenses}</td>
                    {canPrice && <td style={{ ...td, textAlign: "right", color: "#16a34a", fontWeight: 600 }}>{usd(c.cost_usd)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2) Risiko kapasitas */}
      <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anCapacityRisk")}</div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={{ ...S.cardT, color: "#dc2626", marginBottom: 8 }}>{t("m365.anNearFull")}</div>
          {an.capacity.nearFull.length === 0 ? <div style={{ fontSize: 13, color: "#94a3b8" }}>{t("common.noData")}</div> : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>{t("m365.colLicenseType")}</th><th style={{ ...th, textAlign: "center" }}>%</th><th style={{ ...th, textAlign: "center" }}>{t("m365.colAvailable")}</th></tr></thead>
              <tbody>{an.capacity.nearFull.map((c, i) => (
                <tr key={i}><td style={td}>{c.friendly_name}</td>
                  <td style={{ ...td, textAlign: "center", color: c.percent >= 100 ? "#dc2626" : "#d97706", fontWeight: 700 }}>{c.percent}%</td>
                  <td style={{ ...td, textAlign: "center", color: c.available <= 0 ? "#dc2626" : "#0f172a", fontWeight: 600 }}>{c.available}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
        <div style={S.card}>
          <div style={{ ...S.cardT, color: "rgb(var(--brand-600))", marginBottom: 8 }}>{t("m365.anLowUse")}</div>
          {an.capacity.lowUse.length === 0 ? <div style={{ fontSize: 13, color: "#94a3b8" }}>{t("common.noData")}</div> : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>{t("m365.colLicenseType")}</th><th style={{ ...th, textAlign: "center" }}>%</th>{canPrice && <th style={{ ...th, textAlign: "right" }}>{t("m365.anIdleCost")}</th>}</tr></thead>
              <tbody>{an.capacity.lowUse.map((c, i) => (
                <tr key={i}><td style={td}>{c.friendly_name}</td>
                  <td style={{ ...td, textAlign: "center" }}>{c.percent}%</td>
                  {canPrice && <td style={{ ...td, textAlign: "right", color: "#d97706", fontWeight: 600 }}>{c.idle_cost_usd ? usd(c.idle_cost_usd) : "—"}</td>}</tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>

      {/* 3) Biaya per departemen & company (hanya bila berhak lihat harga) */}
      {canPrice && (<>
      <div style={{ ...S.cardT, margin: "16px 0 8px" }}>{t("m365.anCostByOrg")}</div>
      {(an.cost.byDept.length === 0 && an.cost.byCompany.length === 0) ? (
        <div style={{ ...S.card, fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>{t("m365.anNoOrgData")}</div>
      ) : (
        <div style={S.grid2}>
          <div style={S.card}>
            <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anByDept")}</div>
            <ResponsiveContainer width="100%" height={Math.max(160, an.cost.byDept.length * 26)}>
              <BarChart data={an.cost.byDept} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={usd} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip {...TT} formatter={(v) => usd(v)} />
                <Bar dataKey="cost_usd" fill="rgb(var(--brand-600))" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={S.card}>
            <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anByCompany")}</div>
            <ResponsiveContainer width="100%" height={Math.max(160, an.cost.byCompany.length * 26)}>
              <BarChart data={an.cost.byCompany} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={usd} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10, fill: "#64748b" }} />
                <Tooltip {...TT} formatter={(v) => usd(v)} />
                <Bar dataKey="cost_usd" fill="#7c3aed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      </>)}

      {/* 4) Distribusi & anomali */}
      <div style={{ ...S.cardT, margin: "16px 0 8px" }}>{t("m365.anDistribution")}</div>
      <div style={S.grid2}>
        <div style={S.card}>
          <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anLicenseMix")}</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={an.distribution.licenseMix} dataKey="value" nameKey="name" cx="50%" cy="50%"
                innerRadius={55} outerRadius={90} paddingAngle={2} label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""} labelLine={false}>
                {an.distribution.licenseMix.map((e, i) => <Cell key={i} fill={MC[i % MC.length]} />)}
              </Pie>
              <Tooltip {...TT} /><Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={S.card}>
          <div style={{ ...S.cardT, marginBottom: 8 }}>{t("m365.anAnomalies")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", background: "#fef2f2", borderRadius: 8, fontSize: 13 }}>
              <span>{t("m365.anDisabledLicensed")}</span>
              <b style={{ color: "#dc2626" }}>{an.distribution.anomalies.disabledLicensed}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", background: "#fffbeb", borderRadius: 8, fontSize: 13 }}>
              <span>{t("m365.anPaidNoDept")}</span>
              <b style={{ color: "#d97706" }}>{an.distribution.anomalies.paidNoDept}</b>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function M365Dashboard({ dataFetcher } = {}) {
  const { t } = useLang();
  // Override fetch utk mode share (cookie, tanpa JWT). Default: authGet asli
  // (localStorage token) — perilaku login TIDAK berubah sama sekali.
  const get = dataFetcher || authGet;

  // Tab "pricing" sengaja TIDAK ikut deep link: visibilitasnya bergantung pada
  // `canViewPrice`, yang baru tiba setelah fetch. Menerimanya dari URL berarti
  // panel harga sempat tampil sebelum izin diketahui.
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const p = searchParams.get("tab");
    return M365_TABS.includes(p) ? p : "overview";
  });   // overview | detail | pricing | analytics
  useTabDeepLink("m365", (p) => M365_TABS.includes(p), setTab);

  // ── Data overview ──
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState(null);
  const [categories, setCats]   = useState([]);
  const [summary, setSummary]   = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [kpi, setKpi]           = useState({ distinct: 0, assignments: 0, unlicensed: 0 });
  const [capacity, setCapacity] = useState([]);
  const [domainGroups, setDomainGroups] = useState({});
  const [canViewPrice, setCanViewPrice] = useState(false);
  const [dist, setDist]         = useState({ categories: [], groups: [] });
  const [domainCost, setDomainCost] = useState({});   // domain -> { license: cost_usd } — semua grup lokasi
  const [drill, setDrill]       = useState(null);   // {domain, license} | null

  // ── Tabel user (per-license, tab Overview) ──
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(0);
  const [fDomain, setFDomain]   = useState("");
  const [fLicense, setFLicense] = useState("");
  const [search, setSearch]     = useState("");
  const [uBusy, setUBusy]       = useState(false);
  const [uSort, setUSort]       = useState({ key: null, dir: "asc" });   // sort server-side per kolom
  const toggleUSort = (key) => setUSort(s => s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" });

  // ── User Detail tab ──
  const [dRows, setDRows]       = useState([]);
  const [dTotal, setDTotal]     = useState(0);
  const [dPage, setDPage]       = useState(0);
  const [dFDomain, setDFDomain] = useState("");
  const [dFEnabled, setDFEnabled]   = useState("");   // "" | "1" | "0"
  const [dFLicensed, setDFLicensed] = useState("");   // "" | "1" | "0"
  const [dSearch, setDSearch]   = useState("");
  const [dBusy, setDBusy]       = useState(false);
  const [dSnapshot, setDSnapshot] = useState(null);
  const [dCat, setDCat]         = useState("");     // filter kategori lisensi (dari klik sel pivot)
  const [dSort, setDSort]       = useState({ key: null, dir: "asc" });   // sort server-side per kolom
  const toggleDSort = (key) => setDSort(s => s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: "asc" });
  // Pivot Domain x License Category
  const [pivot, setPivot]       = useState(null);
  const [pivotBusy, setPivotBusy] = useState(false);
  const [pivotOpen, setPivotOpen] = useState(true);
  const [pvSort, setPvSort]     = useState({ key: "total", dir: "desc" });  // sort client-side
  const togglePvSort = (key) => setPvSort(s => s.key !== key ? { key, dir: key === "domain" ? "asc" : "desc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" });

  // ── Pricing tab (master price per jenis lisensi) ──
  const [pRows, setPRows]       = useState([]);
  const [pCanEdit, setPCanEdit] = useState(false);
  const [pBusy, setPBusy]       = useState(false);
  const [pEdits, setPEdits]     = useState({});   // sku_part -> {price_usd,notes}
  const [pSaving, setPSaving]   = useState({});   // sku_part -> bool
  const [pSavingAll, setPSavingAll] = useState(false);
  const [pCalcBusy, setPCalcBusy] = useState(false);
  const [pCalcMsg, setPCalcMsg]   = useState(null);

  // ── Analytics tab ──
  const [an, setAn]         = useState(null);
  const [anBusy, setAnBusy] = useState(false);

  const loadSummary = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await get("/api/m365/summary");
      setCats(d.categories || []);
      setSummary(d.summary || []);
      setSnapshot(d.snapshot || null);
      setKpi(d.users || { distinct: 0, assignments: 0, unlicensed: 0 });
      setDomainGroups(d.domainGroups || {});
      setCanViewPrice(!!d.canViewPrice);
      try { const c = await get("/api/m365/capacity"); setCapacity(c.data || []); } catch { /* opsional */ }
      try { const g = await get("/api/m365/distribution"); setDist(g || { categories: [], groups: [] }); } catch { /* opsional */ }
      try {
        const dc = await get("/api/m365/domain-cost");
        const map = {};
        (dc.data || []).forEach(r => { (map[r.domain] = map[r.domain] || {})[r.license] = r.cost_usd; });
        setDomainCost(map);
      } catch { /* opsional */ }
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadUsers = useCallback(async (p = 0) => {
    setUBusy(true);
    try {
      const qs = new URLSearchParams({ limit: String(PAGE), offset: String(p * PAGE) });
      if (fDomain)  qs.set("domain", fDomain);
      if (fLicense) qs.set("license", fLicense);
      if (search.trim()) qs.set("search", search.trim());
      if (uSort.key) { qs.set("sort", uSort.key); qs.set("dir", uSort.dir); }
      const d = await get(`/api/m365/users?${qs.toString()}`);
      setRows(d.data || []);
      setTotal(d.total || 0);
    } catch (e) { setErr(e.message); }
    finally { setUBusy(false); }
  }, [fDomain, fLicense, search, uSort]);

  const loadDetail = useCallback(async (p = 0) => {
    setDBusy(true);
    try {
      const qs = new URLSearchParams({ limit: String(PAGE), offset: String(p * PAGE) });
      if (dFDomain) qs.set("domain", dFDomain);
      if (dFEnabled) qs.set("enabled", dFEnabled);
      if (dFLicensed) qs.set("licensed", dFLicensed);
      if (dSearch.trim()) qs.set("search", dSearch.trim());
      if (dCat) qs.set("category", dCat);
      if (dSort.key) { qs.set("sort", dSort.key); qs.set("dir", dSort.dir); }
      const d = await get(`/api/m365/user-detail?${qs.toString()}`);
      setDRows(d.data || []);
      setDTotal(d.total || 0);
      setDSnapshot(d.snapshot || null);
    } catch (e) { setErr(e.message); }
    finally { setDBusy(false); }
  }, [dFDomain, dFEnabled, dFLicensed, dSearch, dCat, dSort]);

  // Pivot sengaja TIDAK memakai filter domain/kategori supaya semua domain tetap terlihat
  // (sel yang diklik menjadi filter tabel di bawahnya).
  const loadPivot = useCallback(async () => {
    setPivotBusy(true);
    try {
      const qs = new URLSearchParams();
      if (dFEnabled) qs.set("enabled", dFEnabled);
      if (dFLicensed) qs.set("licensed", dFLicensed);
      if (dSearch.trim()) qs.set("search", dSearch.trim());
      setPivot(await get(`/api/m365/user-detail/pivot?${qs.toString()}`));
    } catch (e) { setErr(e.message); }
    finally { setPivotBusy(false); }
  }, [dFEnabled, dFLicensed, dSearch]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setPage(0); loadUsers(0); }, [fDomain, fLicense, uSort]); // eslint-disable-line
  useEffect(() => { if (tab === "detail") { setDPage(0); loadDetail(0); } }, [tab, dFDomain, dFEnabled, dFLicensed, dCat, dSort]); // eslint-disable-line
  useEffect(() => { if (tab === "detail") loadPivot(); }, [tab, dFEnabled, dFLicensed]); // eslint-disable-line

  const loadPricing = useCallback(async () => {
    setPBusy(true);
    try {
      const d = await get("/api/m365/pricing");
      setPRows(d.data || []);
      setPCanEdit(!!d.canEdit);
      setPEdits({});
    } catch (e) { setErr(e.message); }
    finally { setPBusy(false); }
  }, []); // eslint-disable-line

  useEffect(() => { if (tab === "pricing" && pRows.length === 0) loadPricing(); }, [tab]); // eslint-disable-line

  const loadAnalytics = useCallback(async () => {
    setAnBusy(true);
    try { setAn(await get("/api/m365/analytics")); }
    catch (e) { setErr(e.message); }
    finally { setAnBusy(false); }
  }, []); // eslint-disable-line

  useEffect(() => { if (tab === "analytics" && !an) loadAnalytics(); }, [tab]); // eslint-disable-line

  // Harga dipisah per grup lokasi, jadi kunci edit = sku_part|location_group.
  const priceKey  = (r) => `${r.sku_part}|${r.location_group || "ID"}`;
  const editPrice = (row, field, value) => {
    const k = priceKey(row);
    setPEdits(prev => ({ ...prev, [k]: { ...prev[k], [field]: value } }));
  };
  const putOnePrice = (row) => {
    const edit = pEdits[priceKey(row)] || {};
    const rawUsd = edit.price_usd !== undefined ? edit.price_usd : row.price_usd;
    const price_usd = Number(String(rawUsd).replace(",", ".")) || 0;
    const notes      = edit.notes    !== undefined ? edit.notes    : (row.notes || "");
    return fetch(`${API_BASE}/api/m365/pricing/${encodeURIComponent(row.sku_part)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify({ price_usd, notes, location_group: row.location_group || "ID" }),
    }).then(async r => { const j = await r.json().catch(() => ({})); if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`); });
  };
  // Simpan semua baris yang diedit (opsi B): loop sequential ke endpoint PUT per-sku_part yang sudah ada.
  const saveAllPrices = async () => {
    if (dataFetcher) { setErr("Read-only (share mode)"); return; }
    const dirtyRows = pRows.filter(r => pEdits[priceKey(r)]);
    if (!dirtyRows.length) return;
    setPSavingAll(true);
    const failed = [];
    for (const row of dirtyRows) {
      setPSaving(prev => ({ ...prev, [priceKey(row)]: true }));
      try { await putOnePrice(row); }
      catch (e) { failed.push(`${row.sku_part}: ${e.message}`); }
      finally { setPSaving(prev => ({ ...prev, [priceKey(row)]: false })); }
    }
    await loadPricing();
    setPSavingAll(false);
    setErr(failed.length ? failed.join("; ") : null);
  };

  const calculateCost = async () => {
    setPCalcBusy(true); setPCalcMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/m365/pricing/calculate`, {
        method: "POST", headers: { Authorization: `Bearer ${getToken()}` },
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setPCalcMsg(j.message || t("m365.calcDone"));
      // Muat ulang cost utk tabel distribusi Indonesia di tab Overview.
      try {
        const dc = await get("/api/m365/domain-cost");
        const map = {};
        (dc.data || []).forEach(x => { (map[x.domain] = map[x.domain] || {})[x.license] = x.cost_usd; });
        setDomainCost(map);
      } catch { /* opsional */ }
    } catch (e) { setErr(e.message); }
    finally { setPCalcBusy(false); }
  };

  // ── Derivasi chart (Overview) ──
  const domains = useMemo(() => [...new Set(summary.map(r => r.domain))].sort(), [summary]);
  const idDomains = useMemo(() => domains.filter(d => (domainGroups[d] || "NONID") === "ID"), [domains, domainGroups]);
  const barData = useMemo(() => {
    const by = {};
    summary.forEach(r => { (by[r.domain] = by[r.domain] || {})[r.license] = r.count; });
    return idDomains.map(dom => {
      const row = { domain: dom.replace(".onmicrosoft.com", "") };
      categories.forEach(c => { row[c] = by[dom]?.[c] || 0; });
      return row;
    });
  }, [summary, idDomains, categories]);
  // Sumber pie = m365_capacity (assigned dari Graph, tenant-wide) — bukan agregasi
  // per-domain (m365_license_summary), supaya total pie SELALU match dgn tabel
  // Kapasitas Lisensi (satu sumber kebenaran: consumedUnits per SKU dari Graph).
  const pieData = useMemo(() => capacity.map(c => ({
    name: c.friendly_name, value: c.assigned || 0,
  })).filter(d => d.value > 0), [capacity]);
  const pieTotal = useMemo(() => pieData.reduce((a, d) => a + d.value, 0), [pieData]);
  const pieNames = useMemo(() => pieData.map(d => d.name), [pieData]);
  // Domain ID diurutkan dari pemakai terbanyak (bar horizontal lebih mudah dibaca).
  const barSorted = useMemo(() => barData.map(r => ({ ...r, _total: categories.reduce((a, c) => a + (r[c] || 0), 0) }))
    .filter(r => r._total > 0).sort((a, b) => b._total - a._total).map((r, _i, arr) => ({ ...r, _cap: (arr[0]?._total || 1) * 0.002 })), [barData, categories]);
  // Ringkasan seat tenant-wide (sumber: m365_capacity).
  const seat = useMemo(() => {
    const tot = capacity.reduce((a, c) => a + (c.total || 0), 0);
    const asg = capacity.reduce((a, c) => a + (c.assigned || 0), 0);
    return { total: tot, assigned: asg, available: Math.max(0, tot - asg), pct: tot ? Math.round(asg / tot * 1000) / 10 : 0,
             risk: capacity.filter(c => c.percent >= 90).length };
  }, [capacity]);
  const monthlyCost = useMemo(() => Object.values(domainCost).reduce((a, m) => a + Object.values(m).reduce((x, v) => x + (v || 0), 0), 0), [domainCost]);
  const capSorted = useMemo(() => [...capacity].sort((a, b) => b.percent - a.percent), [capacity]);
  const goUnlicensed = () => { setFLicense("Unlicensed"); setTimeout(() => document.getElementById("m365-users")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE));
  const dTotalPages = Math.max(1, Math.ceil(dTotal / PAGE));
  const licenseOptions = useMemo(() => ["", ...categories, "Unlicensed"], [categories]);
  const detailDomains = useMemo(() => [...new Set(summary.map(r => r.domain))].sort(), [summary]);

  const exportExcel = async () => {
    setUBusy(true);
    try {
      const qs = new URLSearchParams({ limit: "100000", offset: "0" });
      if (fDomain)  qs.set("domain", fDomain);
      if (fLicense) qs.set("license", fLicense);
      if (search.trim()) qs.set("search", search.trim());
      if (uSort.key) { qs.set("sort", uSort.key); qs.set("dir", uSort.dir); }
      const d = await get(`/api/m365/users?${qs.toString()}`);
      const ws = XLSX.utils.json_to_sheet((d.data || []).map(r => ({
        UPN: r.upn, Domain: r.domain, License: r.license, "SKU Part": r.sku_part || "", Snapshot: r.snapshot_at,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "M365 Users");
      XLSX.writeFile(wb, `M365_Users_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { setErr(e.message); }
    finally { setUBusy(false); }
  };

  // Export User Detail — lengkap 18 kolom (semua field).
  const exportDetailExcel = async () => {
    setDBusy(true);
    try {
      const qs = new URLSearchParams({ limit: "100000", offset: "0" });
      if (dFDomain) qs.set("domain", dFDomain);
      if (dFEnabled) qs.set("enabled", dFEnabled);
      if (dFLicensed) qs.set("licensed", dFLicensed);
      if (dSearch.trim()) qs.set("search", dSearch.trim());
      if (dCat) qs.set("category", dCat);
      if (dSort.key) { qs.set("sort", dSort.key); qs.set("dir", dSort.dir); }
      const d = await get(`/api/m365/user-detail?${qs.toString()}`);
      const ws = XLSX.utils.json_to_sheet((d.data || []).map(r => ({
        UPN: r.upn, DisplayName: r.display_name, FirstName: r.first_name, LastName: r.last_name,
        Mail: r.mail, JobTitle: r.job_title, Department: r.department, Company: r.company,
        Office: r.office, Mobile: r.mobile, Phone: r.phone,
        Enabled: r.enabled === 1 ? "True" : r.enabled === 0 ? "False" : "",
        UserType: r.user_type, UsageLocation: r.usage_location, Domain: r.domain,
        Created: r.created,
        SyncedFromAD: r.synced_from_ad === 1 ? "True" : r.synced_from_ad === 0 ? "False" : "",
        Licenses: r.licenses,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "M365 User Detail");
      XLSX.writeFile(wb, `M365_UserDetail_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { setErr(e.message); }
    finally { setDBusy(false); }
  };

  const runSearch = () => { setPage(0); loadUsers(0); };
  const gotoPage  = (p) => { setPage(p); loadUsers(p); };
  const runDSearch = () => { setDPage(0); loadDetail(0); loadPivot(); };
  // Pivot: kolom yang seluruhnya 0 disembunyikan; baris diurutkan sesuai header yang diklik.
  const pvCols = useMemo(() => (pivot?.categories || []).filter(c => (pivot?.grand?.counts?.[c] || 0) > 0), [pivot]);
  const pvRows = useMemo(() => {
    const rs = [...(pivot?.rows || [])];
    const val = (r) => pvSort.key === "domain" ? r.domain : pvSort.key === "total" ? r.total : pvSort.key === "enabled" ? r.enabled : (r.counts?.[pvSort.key] || 0);
    rs.sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === "string" ? x.localeCompare(y) : x - y;
      return (pvSort.dir === "asc" ? c : -c) || a.domain.localeCompare(b.domain);
    });
    return rs;
  }, [pivot, pvSort]);
  const pickPivot = (domain, cat) => {
    setDFDomain(domain && domain !== "(no domain)" ? domain : "");
    setDCat(cat || "");
    setTimeout(() => document.getElementById("m365-detail-table")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const exportPivotExcel = () => {
    if (!pivot) return;
    const line = (r) => ({ Domain: r.domain, ...Object.fromEntries(pvCols.map(c => [c, r.counts?.[c] || 0])), "Total users": r.total, Enabled: r.enabled });
    const ws = XLSX.utils.json_to_sheet([...pvRows.map(line), line(pivot.grand)]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Domain x Category");
    XLSX.writeFile(wb, `M365_UserDetail_Pivot_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const gotoDPage  = (p) => { setDPage(p); loadDetail(p); };

  return (
    <div style={S.page}>
      <style>{CSS}</style>
      {/* Hero header */}
      <div style={{ ...S.hdr, position: "relative", overflow: "hidden", borderRadius: 18, padding: "20px 24px", color: "#fff",
                    background: "linear-gradient(120deg, rgb(var(--brand-700)) 0%, rgb(var(--brand-600)) 55%, rgb(var(--brand-500)) 100%)",
                    boxShadow: "0 10px 30px rgb(var(--brand-600) / .25)" }}>
        <div style={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.10)" }} />
        <div style={{ position: "absolute", right: 120, bottom: -70, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 14, position: "relative", minWidth: 0 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,.2)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <LayoutGrid size={26} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ ...S.title, color: "#fff" }}>{t("m365.title")}</h1>
            <div style={{ ...S.sub, color: "rgba(255,255,255,.88)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {snapshot ? (<><CalendarClock size={13} /> {t("m365.lastSnapshot").replace("{snap}", snapshot)}</>) : t("m365.noDataHint")}
              {snapshot && <span style={{ opacity: .7 }}>· {domains.length} domains · {capacity.length} SKUs</span>}
            </div>
          </div>
        </div>
        <button style={{ ...S.btn, position: "relative", background: "rgba(255,255,255,.95)", border: "none", color: "rgb(var(--brand-700))", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,.12)" }}
          onClick={() => { loadSummary(); if (tab === "detail") loadDetail(dPage); else loadUsers(page); }} disabled={loading}>
          <RefreshCw size={15} style={loading ? { animation: "m365Spin 1s linear infinite" } : undefined} /> {t("common.refresh")}
        </button>
      </div>

      {err && (
        <div style={{ ...S.card, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <X size={16} /> {err}
        </div>
      )}

      {/* Tab bar (segmented) */}
      <div style={S.tabBar}>
        <button style={{ ...S.tab, ...(tab === "overview" ? S.tabOn : {}) }} onClick={() => setTab("overview")}><LayoutGrid size={14} /> {t("m365.tabOverview")}</button>
        <button style={{ ...S.tab, ...(tab === "detail" ? S.tabOn : {}) }} onClick={() => setTab("detail")}><UserCog size={14} /> {t("m365.tabUserDetail")}</button>
        {canViewPrice && (
          <button style={{ ...S.tab, ...(tab === "pricing" ? S.tabOn : {}) }} onClick={() => setTab("pricing")}><Tag size={14} /> {t("m365.tabPricing")}</button>
        )}
        <button style={{ ...S.tab, ...(tab === "analytics" ? S.tabOn : {}) }} onClick={() => setTab("analytics")}><Sparkles size={14} /> {t("m365.tabAnalytics")}</button>
      </div>

      {loading ? (
        <div style={{ ...S.card, textAlign: "center", color: "#94a3b8", padding: 40 }}>{t("common.loading")}</div>
      ) : tab === "overview" ? (
        <>
          {/* KPI */}
          <div style={S.kpiGrid}>
            <KpiCard icon={Users}      color="rgb(var(--brand-600))" label={t("m365.kpiTotalUsers")} value={kpi.distinct}
              sub={`${domains.length} domains`} onClick={() => setTab("detail")} />
            <KpiCard icon={BadgeCheck} color="#16a34a" label={t("m365.kpiAssigned")}  value={kpi.assignments}
              sub={kpi.distinct ? `${Math.round((kpi.distinct - kpi.unlicensed) / kpi.distinct * 100)}% of users licensed` : undefined} />
            <KpiCard icon={UserX}      color="#dc2626" label={t("m365.kpiUnlicensed")} value={kpi.unlicensed}
              sub="Click to list users" onClick={goUnlicensed} />
            <KpiCard icon={Gauge}      color="#d97706" label="Seat Utilisation" value={`${seat.pct}%`}
              sub={`${seat.assigned.toLocaleString()} / ${seat.total.toLocaleString()} seats · ${seat.available.toLocaleString()} free`} />
            {canViewPrice
              ? <KpiCard icon={Wallet} color="#7c3aed" label="Licence Cost" value={fmtUsd0(monthlyCost)} sub="All domains · per pricing master"
                  onClick={() => setTab("pricing")} />
              : <KpiCard icon={Globe}  color="#7c3aed" label={t("m365.kpiDomains")} value={domains.length} />}
          </div>

          {/* Charts */}
          <div style={S.grid2}>
            <div style={S.card}>
              <div style={{ ...S.cardT, justifyContent: "space-between" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><BarChart3 size={14} color="rgb(var(--brand-600))" /> {t("m365.chartPerDomain")}</span>
                <span style={S.pill}>{barSorted.length} domains</span>
              </div>
              <ResponsiveContainer width="100%" height={Math.max(260, barSorted.length * 34 + 50)}>
                <BarChart data={barSorted} layout="vertical" margin={{ top: 4, right: 44, left: 4, bottom: 4 }} barCategoryGap={7}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="domain" width={150} tick={{ fontSize: 11.5, fill: "#334155" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => v.replace(/\.(com|co\.id|net)$/, "")} />
                  <Tooltip {...TT} cursor={{ fill: "rgba(148,163,184,.12)" }} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="circle" iconSize={9} />
                  {categories.map((c, i) => (
                    <Bar key={c} dataKey={c} stackId="a" fill={licColor(c, pieNames.includes(c) ? pieNames : categories)}
                      style={{ cursor: "pointer" }}
                      onClick={(d) => d && setDrill({ domain: idDomains.find(x => x.replace(".onmicrosoft.com", "") === d.domain) || d.domain, license: c })}>
                      {null}
                    </Bar>
                  ))}
                  <Bar dataKey="_cap" stackId="a" fill="transparent" legendType="none" tooltipType="none" isAnimationActive={false}>
                    <LabelList dataKey="_total" position="right" offset={6} style={{ fontSize: 11, fontWeight: 700, fill: "#334155" }} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={S.card}>
              <div style={{ ...S.cardT, justifyContent: "space-between" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><PieIcon size={14} color="rgb(var(--brand-600))" /> {t("m365.chartCategoryDist")}</span>
                <span style={S.pill}>{pieTotal.toLocaleString()} licences</span>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
                <div style={{ position: "relative", flex: "0 0 220px", height: 220, margin: "0 auto" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={68} outerRadius={100}
                        paddingAngle={pieData.length > 1 ? 2 : 0} cornerRadius={4} stroke="none">
                        {pieData.map((e, i) => <Cell key={i} fill={MC[i % MC.length]} />)}
                      </Pie>
                      <Tooltip {...TT} formatter={(v, n) => [`${v.toLocaleString()} (${pieTotal ? Math.round(v / pieTotal * 1000) / 10 : 0}%)`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{pieTotal.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, letterSpacing: .8, textTransform: "uppercase", marginTop: 4 }}>{t("m365.totalLicensesLabel")}</div>
                  </div>
                </div>
                <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 6 }}>
                  {pieData.map((s, i) => {
                    const p = pieTotal ? Math.round(s.value / pieTotal * 1000) / 10 : 0;
                    const canFilter = categories.includes(s.name);
                    return (
                      <div key={s.name} onClick={canFilter ? () => { setFLicense(s.name); setTimeout(() => document.getElementById("m365-users")?.scrollIntoView({ behavior: "smooth" }), 50); } : undefined}
                        style={{ cursor: canFilter ? "pointer" : "default", padding: "6px 8px", borderRadius: 8 }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: MC[i % MC.length], flexShrink: 0 }} />
                          <span style={{ flex: 1, color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                          <span style={{ fontWeight: 800, color: "#0f172a" }}>{s.value.toLocaleString()}</span>
                          <span style={{ color: "#94a3b8", width: 44, textAlign: "right" }}>{p}%</span>
                        </div>
                        <div style={{ height: 4, background: "#f1f5f9", borderRadius: 4, marginTop: 5, marginLeft: 18 }}>
                          <div style={{ width: `${p}%`, height: "100%", background: MC[i % MC.length], borderRadius: 4 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Kapasitas lisensi — kartu per SKU */}
          {capacity.length > 0 && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ ...S.cardT, justifyContent: "space-between", flexWrap: "wrap" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Layers size={14} color="rgb(var(--brand-600))" /> {t("m365.capacityTitle")}</span>
                <span style={{ display: "inline-flex", gap: 6 }}>
                  {seat.risk > 0 && <Chip color="#b45309" bg="#fef3c7"><AlertTriangle size={12} /> {seat.risk} near / at full</Chip>}
                  <span style={S.pill}>{seat.assigned.toLocaleString()} / {seat.total.toLocaleString()} seats</span>
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,240px),1fr))", gap: 12 }}>
                {capSorted.map((r, i) => <CapacityCard key={i} row={r} />)}
              </div>
            </div>
          )}

          {/* License Distribution per grup lokasi */}
          {dist.groups.some(g => g.rows.length > 0) && (
            <div style={{ ...S.card, marginBottom: 16 }}>
              <div style={{ ...S.cardT, marginBottom: 14 }}><Table2 size={14} color="rgb(var(--brand-600))" /> {t("m365.distributionTitle")}
                <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 500, color: "#94a3b8", letterSpacing: 0, textTransform: "none" }}>Click a number to list the users</span>
              </div>
              {dist.groups.map(g => (
                <DistTable key={g.key} group={g} categories={dist.categories}
                  onCell={(domain, license) => setDrill({ domain, license })}
                  costMap={canViewPrice ? domainCost : null} />
              ))}
            </div>
          )}

          {/* Tabel user (per-license) */}
          <div id="m365-users" style={{ ...S.card, scrollMarginTop: 80 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
              <div style={{ ...S.cardT, marginBottom: 0 }}><Users size={14} color="rgb(var(--brand-600))" /> {t("m365.perUserTitle")} <span style={S.pill}>{total.toLocaleString()} {t("m365.rows")}</span></div>
              <button style={S.btn} onClick={exportExcel} disabled={uBusy}><Download size={14} /> {t("m365.exportExcel")}</button>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <select style={S.input} value={fDomain} onChange={e => setFDomain(e.target.value)}>
                <option value="">{t("m365.allDomains")}</option>
                {domains.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select style={S.input} value={fLicense} onChange={e => setFLicense(e.target.value)}>
                {licenseOptions.map(l => <option key={l} value={l}>{l === "" ? t("m365.allLicenses") : l}</option>)}
              </select>
              <input
                style={{ ...S.input, minWidth: 200 }}
                placeholder={t("m365.searchUpnDomain")}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
              />
              <button style={{ ...S.btn, background: "rgb(var(--brand-600))", color: "#fff", border: "none", fontWeight: 600 }} onClick={runSearch}><Search size={14} /> {t("m365.search")}</button>
              {(search || fDomain || fLicense) && (
                <button style={S.btn} onClick={() => { setSearch(""); setFDomain(""); setFLicense(""); }}>{t("m365.reset")}</button>
              )}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {[["upn", "UPN", "left"], ["domain", t("m365.colDomain"), "left"], ["license", t("m365.colLicense"), "left"], ["sku_part", t("m365.colSkuPart"), "center"]].map(([k, label, al]) => {
                      const on = uSort.key === k;
                      const Ic = !on ? ArrowUpDown : uSort.dir === "asc" ? ArrowUp : ArrowDown;
                      return (
                        <th key={k} onClick={() => toggleUSort(k)} title="Click to sort"
                          style={{ ...S.th, textAlign: al, cursor: "pointer", userSelect: "none", color: on ? "rgb(var(--brand-700))" : S.th.color, background: on ? "rgb(var(--brand-50))" : S.th.background }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            {label} <Ic size={12} style={{ opacity: on ? 1 : .35 }} />
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: 24 }} colSpan={4}>{t("common.noData")}</td></tr>
                  ) : rows.map((r, i) => (
                    <tr key={i} className="m365-tr">
                      <td style={{ ...S.td, fontWeight: 600 }}>{r.upn}</td>
                      <td style={S.td}><Chip>{r.domain}</Chip></td>
                      <td style={S.td}>
                        {r.license === "Unlicensed"
                          ? <Chip color="#b91c1c" bg="#fee2e2">Unlicensed</Chip>
                          : <Chip color="#0f172a" bg={`color-mix(in srgb, ${licColor(r.license, pieNames.includes(r.license) ? pieNames : categories)} 14%, #fff)`}>
                              <span style={{ width: 7, height: 7, borderRadius: 2, background: licColor(r.license, pieNames.includes(r.license) ? pieNames : categories) }} />{r.license}</Chip>}
                      </td>
                      <td style={{ ...S.td, textAlign: "center", color: "#64748b", fontFamily: "monospace", fontSize: 12 }}>{r.sku_part || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > PAGE && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13 }}>
                <button style={{ ...S.btn, opacity: page === 0 ? .5 : 1 }} disabled={page === 0 || uBusy} onClick={() => gotoPage(page - 1)}>← {t("m365.prev")}</button>
                <span style={{ color: "#64748b" }}>{t("m365.page")} {page + 1} / {totalPages}</span>
                <button style={{ ...S.btn, opacity: page + 1 >= totalPages ? .5 : 1 }} disabled={page + 1 >= totalPages || uBusy} onClick={() => gotoPage(page + 1)}>{t("m365.next")} →</button>
              </div>
            )}
          </div>
        </>
      ) : tab === "detail" ? (
        /* ── Tab User Detail ── */
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <div style={{ ...S.cardT, marginBottom: 0 }}>
              <UserCog size={14} color="rgb(var(--brand-600))" /> {t("m365.userDetailTitle")} <span style={S.pill}>{dTotal.toLocaleString()} {t("m365.rows")}</span>
              {dSnapshot && <span style={{ color: "#cbd5e1", fontWeight: 400, letterSpacing: 0, textTransform: "none" }}> · {dSnapshot}</span>}
            </div>
            <button style={S.btn} onClick={exportDetailExcel} disabled={dBusy}><Download size={14} /> {t("m365.exportExcel")}</button>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <select style={S.input} value={dFDomain} onChange={e => setDFDomain(e.target.value)}>
              <option value="">{t("m365.allDomains")}</option>
              {detailDomains.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select style={S.input} value={dFEnabled} onChange={e => setDFEnabled(e.target.value)}>
              <option value="">{t("m365.allStatus")}</option>
              <option value="1">{t("m365.enabledYes")}</option>
              <option value="0">{t("m365.enabledNo")}</option>
            </select>
            <select style={S.input} value={dFLicensed} onChange={e => setDFLicensed(e.target.value)}>
              <option value="">{t("m365.allLicenseState")}</option>
              <option value="1">{t("m365.licensed")}</option>
              <option value="0">{t("m365.unlicensedOpt")}</option>
            </select>
            <input
              style={{ ...S.input, minWidth: 220 }}
              placeholder={t("m365.searchDetail")}
              value={dSearch}
              onChange={e => setDSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") runDSearch(); }}
            />
            <button style={{ ...S.btn, background: "rgb(var(--brand-600))", color: "#fff", border: "none", fontWeight: 600 }} onClick={runDSearch}><Search size={14} /> {t("m365.search")}</button>
            {(dSearch || dFDomain || dFEnabled || dFLicensed || dCat) && (
              <button style={S.btn} onClick={() => { setDSearch(""); setDFDomain(""); setDFEnabled(""); setDFLicensed(""); setDCat(""); }}>{t("m365.reset")}</button>
            )}
          </div>

          {/* ── Pivot: Domain x License Category ── */}
          <div style={{ border: "1px solid #e8edf5", borderRadius: 12, marginBottom: 14, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", padding: "10px 12px", background: "#f8fafc", borderBottom: pivotOpen ? "1px solid #e8edf5" : "none" }}>
              <div style={{ ...S.cardT, marginBottom: 0 }}>
                <Table2 size={14} color="rgb(var(--brand-600))" /> Pivot · Domain × License Category
                {pivot && <span style={S.pill}>{pivot.grand.total.toLocaleString()} users · {pivot.rows.length} domains</span>}
                {pivotBusy && <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>loading…</span>}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {pivotOpen && <button style={S.btn} onClick={exportPivotExcel} disabled={!pivot}><Download size={14} /> Export</button>}
                <button style={S.btn} onClick={() => setPivotOpen(o => !o)}>{pivotOpen ? "Hide" : "Show"}</button>
              </div>
            </div>
            {pivotOpen && pivot && (
              <div style={{ overflowX: "auto", maxHeight: 460 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {[["domain", "Domain", "left"], ...pvCols.map(c => [c, c, "right"]), ["total", "Total users", "right"], ["enabled", "Enabled", "right"]].map(([k, label, al]) => {
                        const on = pvSort.key === k;
                        const Ic = !on ? ArrowUpDown : pvSort.dir === "asc" ? ArrowUp : ArrowDown;
                        return (
                          <th key={k} onClick={() => togglePvSort(k)} title="Click to sort"
                            style={{ ...S.th, textAlign: al, cursor: "pointer", userSelect: "none", position: "sticky", top: 0, zIndex: 1,
                                     whiteSpace: "normal", minWidth: k === "domain" ? 170 : 86, verticalAlign: "bottom",
                                     color: on ? "rgb(var(--brand-700))" : S.th.color, background: on ? "rgb(var(--brand-50))" : S.th.background }}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                              {label} <Ic size={11} style={{ opacity: on ? 1 : .35, flexShrink: 0 }} />
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {pvRows.map(r => {
                      const rowSel = dFDomain === r.domain;
                      return (
                        <tr key={r.domain} className="m365-tr">
                          <td style={{ ...S.td, fontWeight: 600, cursor: "pointer", color: rowSel && !dCat ? "rgb(var(--brand-700))" : S.td.color }}
                              onClick={() => pickPivot(r.domain, "")} title="Show all users of this domain">{r.domain}</td>
                          {pvCols.map(c => {
                            const n = r.counts?.[c] || 0;
                            const sel = rowSel && dCat === c;
                            return (
                              <td key={c} onClick={() => n && pickPivot(r.domain, c)} title={n ? `${r.domain} · ${c}: ${n} users — click to list` : ""}
                                  style={{ ...S.td, textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: n ? "pointer" : "default",
                                           color: n ? (c === "Unlicensed" ? "#b91c1c" : "#0f172a") : "#cbd5e1",
                                           fontWeight: sel ? 800 : 500, background: sel ? "rgb(var(--brand-100))" : undefined }}>
                                {n ? n.toLocaleString() : "·"}
                              </td>
                            );
                          })}
                          <td style={{ ...S.td, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{r.total.toLocaleString()}</td>
                          <td style={{ ...S.td, textAlign: "right", color: "#15803d", fontVariantNumeric: "tabular-nums" }}>{r.enabled.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td style={{ ...S.td, fontWeight: 800, background: "#f1f5f9", position: "sticky", bottom: 0 }}>Total</td>
                      {pvCols.map(c => (
                        <td key={c} onClick={() => pickPivot("", c)} title={`All domains · ${c} — click to list`}
                            style={{ ...S.td, textAlign: "right", fontWeight: 800, background: !dFDomain && dCat === c ? "rgb(var(--brand-100))" : "#f1f5f9", cursor: "pointer", fontVariantNumeric: "tabular-nums", position: "sticky", bottom: 0 }}>
                          {(pivot.grand.counts?.[c] || 0).toLocaleString()}
                        </td>
                      ))}
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 800, background: "#f1f5f9", fontVariantNumeric: "tabular-nums", position: "sticky", bottom: 0 }}>{pivot.grand.total.toLocaleString()}</td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 800, background: "#f1f5f9", color: "#15803d", fontVariantNumeric: "tabular-nums", position: "sticky", bottom: 0 }}>{pivot.grand.enabled.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
                <div style={{ fontSize: 11.5, color: "#94a3b8", padding: "8px 12px" }}>
                  Users holding more than one category are counted in each category column; "Total users" counts each user once. Follows the Status / License / Search filters above. Click a number to list those users below.
                </div>
              </div>
            )}
          </div>

          {dCat && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 12.5, color: "#475569" }}>
              Filtered by category:
              <Chip color="rgb(var(--brand-700))" bg="rgb(var(--brand-50))">{dCat}{dFDomain ? ` · ${dFDomain}` : ""}</Chip>
              <button style={{ ...S.btn, padding: "3px 8px", fontSize: 12 }} onClick={() => setDCat("")}><X size={12} /> Clear</button>
            </div>
          )}

          <div id="m365-detail-table" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[["upn", "UPN", "left"], ["display_name", t("m365.colDisplayName"), "left"], ["department", t("m365.colDepartment"), "left"],
                    ["company", t("m365.colCompany"), "left"], ["enabled", t("m365.colEnabled"), "center"], ["licenses", t("m365.colLicenses"), "left"]].map(([k, label, al]) => {
                    const on = dSort.key === k;
                    const Ic = !on ? ArrowUpDown : dSort.dir === "asc" ? ArrowUp : ArrowDown;
                    return (
                      <th key={k} onClick={() => toggleDSort(k)} title="Click to sort"
                        style={{ ...S.th, textAlign: al, cursor: "pointer", userSelect: "none", color: on ? "rgb(var(--brand-700))" : S.th.color, background: on ? "rgb(var(--brand-50))" : S.th.background }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                          {label} <Ic size={12} style={{ opacity: on ? 1 : .35 }} />
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {dRows.length === 0 ? (
                  <tr><td style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: 24 }} colSpan={6}>{t("common.noData")}</td></tr>
                ) : dRows.map((r, i) => (
                  <tr key={i} className="m365-tr">
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.upn}</td>
                    <td style={S.td}>{r.display_name || "—"}</td>
                    <td style={S.td}>{r.department || "—"}</td>
                    <td style={S.td}>{r.company || "—"}</td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      {r.enabled === 1
                        ? <Chip color="#15803d" bg="#dcfce7">● {t("m365.enabledYes")}</Chip>
                        : <Chip color="#b91c1c" bg="#fee2e2">● {t("m365.enabledNo")}</Chip>}
                    </td>
                    <td style={S.td}>
                      {r.licenses === "Unlicensed" ? <Chip color="#b91c1c" bg="#fee2e2">Unlicensed</Chip> : <span style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{r.licenses || "—"}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {dTotal > PAGE && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, fontSize: 13 }}>
              <button style={{ ...S.btn, opacity: dPage === 0 ? .5 : 1 }} disabled={dPage === 0 || dBusy} onClick={() => gotoDPage(dPage - 1)}>← {t("m365.prev")}</button>
              <span style={{ color: "#64748b" }}>{t("m365.page")} {dPage + 1} / {dTotalPages}</span>
              <button style={{ ...S.btn, opacity: dPage + 1 >= dTotalPages ? .5 : 1 }} disabled={dPage + 1 >= dTotalPages || dBusy} onClick={() => gotoDPage(dPage + 1)}>{t("m365.next")} →</button>
            </div>
          )}
        </div>
      ) : tab === "pricing" ? (
        /* ── Tab Pricing (master price per jenis lisensi) ── */
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
            <div style={{ ...S.cardT, marginBottom: 0 }}><Tag size={14} color="rgb(var(--brand-600))" /> {t("m365.pricingTitle")} <span style={S.pill}>{pRows.length} {t("m365.rows")}</span></div>
            <div style={{ display: "flex", gap: 8 }}>
              {pCanEdit && (
                <button style={{ ...S.btn, borderColor: "#16a34a", color: "#16a34a" }} onClick={calculateCost} disabled={pCalcBusy}>
                  🧮 {pCalcBusy ? "…" : t("m365.calculate")}
                </button>
              )}
              {pCanEdit && (
                <button
                  style={{ ...S.btn, borderColor: "rgb(var(--brand-600))", color: "rgb(var(--brand-600))", opacity: (pSavingAll || pRows.filter(r => pEdits[priceKey(r)]).length === 0) ? .5 : 1 }}
                  onClick={saveAllPrices}
                  disabled={pSavingAll || pRows.filter(r => pEdits[priceKey(r)]).length === 0}
                >
                  {pSavingAll ? "…" : `💾 ${t("m365.saveAll")}`}
                  {!pSavingAll && pRows.filter(r => pEdits[priceKey(r)]).length > 0 ? ` (${pRows.filter(r => pEdits[priceKey(r)]).length})` : ""}
                </button>
              )}
              <button style={S.btn} onClick={loadPricing} disabled={pBusy}>
                <RefreshCw size={14} /> {t("m365.refresh")}
              </button>
            </div>
          </div>

          {pCalcMsg && (
            <div style={{ fontSize: 12, color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "6px 10px", marginBottom: 10 }}>
              {pCalcMsg} {t("m365.calcHint")}
            </div>
          )}

          {!pCanEdit && (
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>{t("m365.pricingReadOnlyHint")}</div>
          )}

          {[{ key: "ID", label: "Indonesia" }, { key: "NONID", label: "Non Indonesia (PILgroup)" }].map((grp) => {
            const gRows = pRows.filter((r) => (r.location_group || "ID") === grp.key);
            return (
            <div key={grp.key} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
                {grp.label} · {gRows.length} {t("m365.rows")}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={S.th}>{t("m365.colLicenseType")}</th>
                      <th style={{ ...S.th, textAlign: "center" }}>{t("m365.colCategory")}</th>
                      <th style={{ ...S.th, textAlign: "right" }}>{t("m365.colPriceUsd")}</th>
                      <th style={S.th}>{t("m365.colNotes")}</th>
                      <th style={S.th}>{t("m365.colUpdated")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gRows.length === 0 ? (
                      <tr><td style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: 24 }} colSpan={5}>{t("common.noData")}</td></tr>
                    ) : gRows.map((r) => {
                      const k     = priceKey(r);
                      const edit  = pEdits[k] || {};
                      const usd   = edit.price_usd !== undefined ? edit.price_usd : (r.price_usd ?? 0);
                      const notes = edit.notes     !== undefined ? edit.notes     : (r.notes || "");
                      const dirty = edit.price_usd !== undefined || edit.notes !== undefined;
                      return (
                        <tr key={k} style={{ background: dirty ? "#fffbeb" : undefined }}>
                          <td style={S.td}>
                            <div style={{ fontWeight: 600 }}>{dirty ? "● " : ""}{r.friendly_name}</div>
                            <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>{r.sku_part}</div>
                          </td>
                          <td style={{ ...S.td, textAlign: "center" }}>
                            {r.is_category ? <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 600 }}>{t("m365.yes")}</span> : <span style={{ color: "#cbd5e1", fontSize: 12 }}>—</span>}
                          </td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            {pCanEdit ? (
                              <input type="text" inputMode="decimal" style={{ ...S.input, width: 110, textAlign: "right" }}
                                value={usd} onChange={(e) => editPrice(r, "price_usd", e.target.value)} />
                            ) : `$${Number(r.price_usd ?? 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 })}`}
                          </td>
                          <td style={S.td}>
                            {pCanEdit ? (
                              <input type="text" style={{ ...S.input, width: 180 }} maxLength={500}
                                value={notes} onChange={(e) => editPrice(r, "notes", e.target.value)} />
                            ) : (r.notes || "—")}
                          </td>
                          <td style={{ ...S.td, fontSize: 11, color: "#94a3b8" }}>
                            {r.updated_at ? <>{r.updated_at}<br />{r.updated_by}</> : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        /* ── Tab Analytics (potret kondisi terkini) ── */
        anBusy && !an ? (
          <div style={{ ...S.card, textAlign: "center", color: "#94a3b8", padding: 40 }}>{t("common.loading")}</div>
        ) : !an ? (
          <div style={{ ...S.card, textAlign: "center", color: "#94a3b8", padding: 40 }}>{t("common.noData")}</div>
        ) : (
          <AnalyticsTab an={an} t={t} onRefresh={loadAnalytics} busy={anBusy} />
        )
      )}

      {drill && <DrillModal ctx={drill} t={t} onClose={() => setDrill(null)} dataFetcher={dataFetcher} />}
    </div>
  );
}
